/**
 * Shared KPI and projection logic for property analysis.
 * Used by both the owner view and the visitor view.
 */
import {
  calculateMortgagePayment,
  calculateMortgageBalance,
  getPaymentsMadeAfterYears
} from './mortgageCalculations';

export function calculateOverviewKPIs(property) {
  if (!property) return null;

  let totalMonthlyRevenue = property?.total_monthly_revenue || 0;
  if ((!totalMonthlyRevenue || totalMonthlyRevenue === 0) && property?.unit_revenues?.length > 0) {
    totalMonthlyRevenue = property.unit_revenues.reduce((sum, unit) => {
      return sum + (parseFloat(unit.monthly_rent) || 0);
    }, 0);
  }

  const monthlyCashflow = property?.monthly_cashflow ?? 0;
  const capRate = property?.cap_rate ?? 0;
  const noi = property?.noi ?? 0;

  const totalInvestment = property?.total_initial_investment || property?.down_payment_amount || 0;
  const annualCashflow = monthlyCashflow * 12;
  const cashOnCashReturn = totalInvestment > 0 ? (annualCashflow / totalInvestment) * 100 : 0;

  const annualRevenue = totalMonthlyRevenue * 12;
  const annualExpenses = (property?.total_monthly_expenses || 0) * 12;

  const mortgageAmountForDSCR = property?.mortgage_amount || 0;
  const interestRateForDSCR = property?.interest_rate || 0;
  const amortizationYearsForDSCR = property?.amortization_period || 25;
  const paymentFrequencyForDSCR = property?.payment_frequency || 'monthly';

  const mortgagePaymentForDSCR = calculateMortgagePayment(
    mortgageAmountForDSCR,
    interestRateForDSCR,
    amortizationYearsForDSCR,
    paymentFrequencyForDSCR
  );
  const annualMortgagePaymentForDSCR = mortgagePaymentForDSCR.annualPayment;
  const dscr = annualMortgagePaymentForDSCR > 0 ? (noi / annualMortgagePaymentForDSCR) : 0;

  const expenseRatio = annualRevenue > 0 ? (annualExpenses / annualRevenue) * 100 : 0;
  const monthlyMortgage = mortgagePaymentForDSCR.annualPayment / 12;

  // ROI total moyen (CAGR) sur 30 ans
  const projYears = 30;
  const purchasePrice = property?.purchase_price || 0;
  const appreciationRate = (property?.appreciation_rate || 3) / 100;
  const rentGrowth = (property?.rent_growth_rate || 2) / 100;
  const expenseGrowthRate = 0.02;

  let cumulativeCashflow = 0;
  for (let i = 0; i < projYears; i++) {
    const yRevenue = annualRevenue * Math.pow(1 + rentGrowth, i + 1);
    const yExpenses = annualExpenses * Math.pow(1 + expenseGrowthRate, i + 1);
    const yNOI = yRevenue - yExpenses;
    const yMortgage = (i + 1) > amortizationYearsForDSCR ? 0 : annualMortgagePaymentForDSCR;
    cumulativeCashflow += (yNOI - yMortgage);
  }
  const finalPropertyValue = purchasePrice * Math.pow(1 + appreciationRate, projYears);
  const valueAppreciation = finalPropertyValue - purchasePrice;
  const finalMortgageBalance = calculateMortgageBalance(
    mortgageAmountForDSCR, interestRateForDSCR, amortizationYearsForDSCR,
    paymentFrequencyForDSCR, getPaymentsMadeAfterYears(projYears, paymentFrequencyForDSCR)
  );
  const principalPaydown = mortgageAmountForDSCR - Math.max(0, finalMortgageBalance);
  const totalROIAmount = cumulativeCashflow + valueAppreciation + principalPaydown;
  const totalROIDecimal = totalInvestment > 0 ? totalROIAmount / totalInvestment : 0;
  const avgAnnualROI = (1 + totalROIDecimal) > 0
    ? (Math.pow(1 + totalROIDecimal, 1 / projYears) - 1) * 100
    : 0;

  return {
    monthlyCashflow,
    annualCashflow,
    capRate,
    cashOnCashReturn,
    cashOnCash: cashOnCashReturn,
    dscr,
    expenseRatio,
    noi,
    totalMonthlyRevenue,
    totalMonthlyExpenses: property?.total_monthly_expenses || 0,
    mortgagePayment: monthlyMortgage,
    avgAnnualROI
  };
}

export function generateOverviewProjectionData(property, kpis, years = 10) {
  if (!property || !kpis) return [];

  const rentGrowth = (property?.rent_growth_rate || 2) / 100;
  const appreciationRate = (property?.appreciation_rate || 3) / 100;
  const expenseGrowth = 0.02;

  let totalMonthlyRevenue = property?.total_monthly_revenue || 0;
  if ((!totalMonthlyRevenue || totalMonthlyRevenue === 0) && property?.unit_revenues?.length > 0) {
    totalMonthlyRevenue = property.unit_revenues.reduce((sum, unit) => {
      return sum + (parseFloat(unit.monthly_rent) || 0);
    }, 0);
  }

  const purchasePrice = property?.purchase_price || 0;
  const mortgageAmount = property?.mortgage_amount || 0;
  const interestRate = property?.interest_rate || 0;
  const amortizationYears = property?.amortization_period || 25;
  const paymentFrequency = property?.payment_frequency || 'monthly';

  const mortgageCalc = calculateMortgagePayment(
    mortgageAmount,
    interestRate,
    amortizationYears,
    paymentFrequency
  );
  const annualMortgagePayment = mortgageCalc.annualPayment;

  const data = [];
  for (let i = 0; i <= years; i++) {
    const annualRevenue = totalMonthlyRevenue * 12 * Math.pow(1 + rentGrowth, i);
    const annualExpenses = (property?.total_monthly_expenses || 0) * 12 * Math.pow(1 + expenseGrowth, i);
    const yearNOI = annualRevenue - annualExpenses;

    const paymentsMade = getPaymentsMadeAfterYears(i, paymentFrequency);
    const isMortgagePaid = i > amortizationYears;
    const actualMortgagePayment = isMortgagePaid ? 0 : annualMortgagePayment;
    const yearCashflow = yearNOI - actualMortgagePayment;

    const mortgageBalance = calculateMortgageBalance(
      mortgageAmount,
      interestRate,
      amortizationYears,
      paymentFrequency,
      paymentsMade
    );
    const propertyValue = purchasePrice * Math.pow(1 + appreciationRate, i);

    data.push({
      year: i,
      propertyValue,
      mortgageBalance: Math.max(0, mortgageBalance),
      equity: propertyValue - Math.max(0, mortgageBalance),
      annualCashflow: yearCashflow,
      noi: yearNOI
    });
  }

  return data;
}

/**
 * Score 0..100 (même logique que ton HeroVerdictCard et SimplifiedOverview)
 */
export function calculateDealScorePercent(property, userProfile) {
  const kpis = calculateOverviewKPIs(property);
  if (!property || !kpis) return null;

  let points = 0;
  let maxPoints = 0;

  // ========== RENDEMENT ==========
  // Cashflow (25 points max)
  const cashflow = kpis.monthlyCashflow || 0;
  maxPoints += 25;
  if (cashflow >= 500) points += 25;
  else if (cashflow >= 300) points += 20;
  else if (cashflow >= 100) points += 15;
  else if (cashflow >= 0) points += 10;
  else if (cashflow >= -200) points += 5;

  // Cash-on-Cash (25 points max)
  const coc = kpis.cashOnCashReturn || 0;
  maxPoints += 25;
  if (coc >= 12) points += 25;
  else if (coc >= 10) points += 22;
  else if (coc >= 8) points += 18;
  else if (coc >= 6) points += 14;
  else if (coc >= 4) points += 10;
  else if (coc >= 2) points += 5;

  // Cap Rate (20 points max)
  const capRate = kpis.capRate || 0;
  maxPoints += 20;
  if (capRate >= 8) points += 20;
  else if (capRate >= 6) points += 16;
  else if (capRate >= 5) points += 12;
  else if (capRate >= 4) points += 8;
  else if (capRate >= 3) points += 4;

  // ========== RISQUE ==========
  // DSCR
  const dscr = kpis.dscr || 0;
  maxPoints += 20;
  if (dscr >= 1.5) points += 20;
  else if (dscr >= 1.3) points += 16;
  else if (dscr >= 1.2) points += 12;
  else if (dscr >= 1.1) points += 8;
  else if (dscr >= 1.0) points += 4;

  // Expense ratio
  const expenseRatio = kpis.expenseRatio || 50;
  maxPoints += 10;
  if (expenseRatio <= 35) points += 10;
  else if (expenseRatio <= 45) points += 8;
  else if (expenseRatio <= 55) points += 5;
  else if (expenseRatio <= 65) points += 2;

  return Math.round((points / maxPoints) * 100);
}

/**
 * Couleurs UI selon le Deal Score (0..100)
 * Retourne une string de classes Tailwind (texte + bg + border)
 */
export function getDealScoreColor(scorePercent) {
  const n = Number(scorePercent);

  if (!Number.isFinite(n)) {
    return "text-muted-foreground bg-muted/30 border-muted";
  }

  if (n >= 80) return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (n >= 60) return "text-green-700 bg-green-50 border-green-200";
  if (n >= 40) return "text-yellow-800 bg-yellow-50 border-yellow-200";
  if (n >= 20) return "text-orange-800 bg-orange-50 border-orange-200";
  return "text-red-700 bg-red-50 border-red-200";
}

/**
 * Badge IA basé sur property.aiAnalysis.recommandation.niveau
 */
export function getAiRecommendationBadge(property) {
  const niveau = property?.aiAnalysis?.recommandation?.niveau?.toUpperCase?.();
  if (!niveau) return null;

  if (niveau.includes('EXCELLENT')) return { label: 'EXCELLENT', bg: 'var(--color-success)' };
  if (niveau.includes('BON')) return { label: 'BON', bg: 'var(--color-primary)' };
  if (niveau.includes('MOYEN')) return { label: 'MOYEN', bg: 'var(--color-warning)' };
  if (niveau.includes('FAIBLE') || niveau.includes('MAUVAIS')) {
    return { label: 'FAIBLE', bg: 'var(--color-destructive)' };
  }
  return null;
}
